import { Download, FileText, Paperclip, RefreshCw, ShieldCheck } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { telechargerPiece, useActionsPieces, usePiecesJointes } from '@/api/hooks';
import { LIBELLE_TYPE_PIECE, type ObjetPiece, type PieceJointe, type TypePiece } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { cn, dateHeureFr } from '@/lib/utils';

/** Formats acceptés par l'API (signature du fichier vérifiée côté serveur). */
export const FORMATS_ACCEPTES = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx,.doc,.xls';
export const TAILLE_MAX_MO = 20;

export const tailleLisible = (n: number): string => (n < 1024 ? `${n} o` : n < 1024 * 1024 ? `${Math.round(n / 1024)} Ko` : `${(n / 1024 / 1024).toFixed(1)} Mo`);
export const messageErreurFichier = (e: unknown): string => (e instanceof Error ? (e.message === 'File too large' ? `Fichier trop volumineux (${TAILLE_MAX_MO} Mo au plus).` : e.message) : 'Action impossible.');

/** Champ de sélection d'un fichier, avec le rappel des formats et de la taille acceptés. */
export function ChampFichier({ id, valeur, onChange, obligatoire, inputRef }: { id: string; valeur: File | null; onChange: (f: File | null) => void; obligatoire?: boolean; inputRef?: React.RefObject<HTMLInputElement | null> }) {
  return (
    <div className="space-y-1">
      <Input id={id} ref={inputRef} type="file" accept={FORMATS_ACCEPTES} required={obligatoire} onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
      <div className="text-xs text-muted-foreground">
        {valeur ? `${valeur.name} · ${tailleLisible(valeur.size)}` : `PDF, image, Word ou Excel · ${TAILLE_MAX_MO} Mo au plus`}
      </div>
    </div>
  );
}

function Ligne({ p, peutRemplacer, onRemplacer }: { p: PieceJointe; peutRemplacer: boolean; onRemplacer: (p: PieceJointe) => void }) {
  const telecharger = async () => {
    try {
      await telechargerPiece(p);
    } catch (e) {
      toast.error(messageErreurFichier(e));
    }
  };
  return (
    <li className={cn('flex flex-wrap items-start gap-2 py-2', p.remplacee && 'opacity-60')}>
      {p.type === 'DOCUMENT_SIGNE' ? <ShieldCheck className="mt-0.5 size-4 shrink-0 text-emerald-700" /> : <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
      <div className="min-w-0 flex-1">
        <button type="button" className="max-w-full truncate text-left font-medium text-primary hover:underline" onClick={telecharger} title="Télécharger">
          {p.nomFichier}
        </button>
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <Badge variant={p.type === 'DOCUMENT_SIGNE' ? 'outline' : 'secondary'} className={cn(p.type === 'DOCUMENT_SIGNE' && 'border-emerald-300 bg-emerald-50 text-emerald-800')}>
            {LIBELLE_TYPE_PIECE[p.type]}
          </Badge>
          {p.apresSoumission && <Badge variant="outline">ajoutée après soumission</Badge>}
          {p.version > 1 && <Badge variant="outline">version {p.version}</Badge>}
          {p.remplacee && <Badge variant="outline">remplacée</Badge>}
          <span>
            {tailleLisible(p.taille)} · {p.deposePar?.nom ?? '—'} · {dateHeureFr(p.createdAt)}
          </span>
        </div>
        {p.commentaire && <div className="text-xs">{p.commentaire}</div>}
      </div>
      <div className="flex gap-1">
        <Button type="button" variant="ghost" size="icon" onClick={telecharger} aria-label="Télécharger">
          <Download className="size-4" />
        </Button>
        {peutRemplacer && !p.remplacee && p.type !== 'DOCUMENT_SIGNE' && (
          <Button type="button" variant="ghost" size="icon" onClick={() => onRemplacer(p)} aria-label="Remplacer par une nouvelle version" title="Remplacer (l'ancienne version reste consultable)">
            <RefreshCw className="size-4" />
          </Button>
        )}
      </div>
    </li>
  );
}

/**
 * Pièces jointes d'un document : liste (versions remplacées conservées), téléchargement, dépôt et remplacement
 * selon les droits renvoyés par l'API (émetteur en rédaction ; contrôle de gestion, DGA et DG après soumission…).
 */
export function PiecesJointes({ objetType, objetId, typesSuggeres, titre = 'Pièces jointes', description }: { objetType: ObjetPiece; objetId: string; typesSuggeres?: TypePiece[]; titre?: string; description?: string }) {
  const { data, isLoading } = usePiecesJointes(objetType, objetId);
  const actions = useActionsPieces(objetType, objetId);
  const [type, setType] = useState<TypePiece>(typesSuggeres?.[0] ?? 'AUTRE');
  const [commentaire, setCommentaire] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const [aRemplacer, setARemplacer] = useState<PieceJointe | null>(null);
  const champ = useRef<HTMLInputElement | null>(null);
  const champRemplacement = useRef<HTMLInputElement | null>(null);

  const deposer = async () => {
    if (!fichier) return;
    try {
      await actions.deposer.mutateAsync({ fichier, type, commentaire: commentaire.trim() || undefined });
      toast.success('Pièce jointe ajoutée.');
      setFichier(null);
      setCommentaire('');
      if (champ.current) champ.current.value = '';
    } catch (e) {
      toast.error(messageErreurFichier(e));
    }
  };
  const remplacer = async (f: File | null) => {
    if (!f || !aRemplacer) return;
    try {
      await actions.remplacer.mutateAsync({ id: aRemplacer.id, fichier: f });
      toast.success("Pièce remplacée ; l'ancienne version reste consultable.");
    } catch (e) {
      toast.error(messageErreurFichier(e));
    } finally {
      setARemplacer(null);
      if (champRemplacement.current) champRemplacement.current.value = '';
    }
  };

  const pieces = data?.pieces ?? [];
  const actives = pieces.filter((p) => !p.remplacee);
  const remplacees = pieces.filter((p) => p.remplacee);
  // La version signée est générée par l'application à l'autorisation : jamais déposée à la main.
  const manuels = (Object.keys(LIBELLE_TYPE_PIECE) as TypePiece[]).filter((t) => t !== 'DOCUMENT_SIGNE');
  const types = typesSuggeres?.length ? [...typesSuggeres.filter((t) => t !== 'DOCUMENT_SIGNE'), ...manuels.filter((t) => !typesSuggeres.includes(t))] : manuels;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="size-4" /> {titre} {!isLoading && <span className="text-sm font-normal text-muted-foreground">({actives.length})</span>}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isLoading ? (
          <div className="text-muted-foreground">Chargement…</div>
        ) : actives.length === 0 ? (
          <div className="text-muted-foreground">Aucune pièce jointe.</div>
        ) : (
          <ul className="divide-y">
            {actives.map((p) => (
              <Ligne key={p.id} p={p} peutRemplacer={Boolean(data?.peutJoindre)} onRemplacer={(x) => { setARemplacer(x); champRemplacement.current?.click(); }} />
            ))}
          </ul>
        )}
        {remplacees.length > 0 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">Versions remplacées ({remplacees.length})</summary>
            <ul className="divide-y">
              {remplacees.map((p) => (
                <Ligne key={p.id} p={p} peutRemplacer={false} onRemplacer={() => undefined} />
              ))}
            </ul>
          </details>
        )}
        <input ref={champRemplacement} type="file" accept={FORMATS_ACCEPTES} className="hidden" onChange={(e) => remplacer(e.target.files?.[0] ?? null)} aria-label="Nouvelle version" />
        {data?.peutJoindre && (
          <div className="space-y-2 rounded-lg border p-3">
            <div className="grid gap-2 sm:grid-cols-[1fr_12rem]">
              <div className="space-y-1.5">
                <Label htmlFor={`piece-${objetId}`}>Joindre un fichier{data.apresSoumission ? ' (mention « ajoutée après soumission »)' : ''}</Label>
                <ChampFichier id={`piece-${objetId}`} valeur={fichier} onChange={setFichier} inputRef={champ} />
              </div>
              <div className="space-y-1.5">
                <Label>Nature</Label>
                <SelectNatif value={type} onChange={(e) => setType(e.target.value as TypePiece)}>
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {LIBELLE_TYPE_PIECE[t]}
                    </option>
                  ))}
                </SelectNatif>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor={`commentaire-${objetId}`}>Commentaire (facultatif)</Label>
                <Input id={`commentaire-${objetId}`} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={500} />
              </div>
              <Button type="button" onClick={deposer} disabled={!fichier || actions.deposer.isPending}>
                <Paperclip className="size-4" /> Joindre
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
